import React from 'react';

import { makeStyles } from 'tss-react/mui';
import MUIChip, {
  ChipProps as MUIChipProps, ChipTypeMap as MUIChipTypeMap
} from '@mui/material/Chip';

const useStyles = makeStyles()({
  labelBold: {
    fontWeight: 500,
  },
  labelThin: {
    fontWeight: 400,
  }
});

const useChipColorsClasses = makeStyles({ name: 'LightChip' })(theme => ({
  default: {
    backgroundColor: theme.palette.grey[200],

    '&:focus': {
      backgroundColor: theme.palette.grey[200],
    },
    '& svg': {
      color: `${theme.palette.text.disabled}`,
    }
  },
  lightGrey: {
    backgroundColor: theme.palette.grey[100],
    '& svg': {
      color: `${theme.palette.text.disabled}`,
    }
  },
  primary: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.legacyPrimary.background,
  },
  secondary: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.secondary.background,
  },
  success: {
    backgroundColor: theme.palette.success.background,

    '& svg': {
      color: `${theme.palette.success.main} !important`,
    }
  },
  successLight: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.text.primary,
  },
  betterhelp: {
    backgroundColor: theme.palette.betterhelp.background,
    color: theme.palette.betterhelp.dark,
  },
  info: {
    color: theme.palette.info.darkText,
    backgroundColor: theme.palette.info.background,
    '& svg': {
      color: `${theme.palette.info.main}`,
    }
  },
  warning: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.warning.background,

    '&:focus, &:active': {
      backgroundColor: theme.palette.warning.background,
    },

    '& svg': {
      color: theme.palette.warning.main,

      '&:focus, &:active, &:hover': {
        color: theme.palette.warning.dark,
      },
    }
  },
  error: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.error.background,
    '& svg': {
      color: `${theme.palette.error.main}`,
    }
  },
}));


export type LightChipProps<
  D extends React.ElementType = MUIChipTypeMap['defaultComponent'],
> = Omit<MUIChipProps<D>, 'color'> & {
  color?: keyof ReturnType<typeof useChipColorsClasses>['classes'];
  elementRef?: React.Ref<HTMLElement>;
  testId?: string;
  labelWeight?: 'bold' | 'thin';
};


export function LightChip_<D extends React.ElementType = MUIChipTypeMap['defaultComponent']>({ color = 'default', className, testId = 'light-chip', labelWeight = 'bold', ...props }: LightChipProps<D>, ref: React.Ref<HTMLDivElement>) {
  const { classes, cx } = useStyles();
  const { classes: colorClasses } = useChipColorsClasses();
  const colorClassName = colorClasses[color];

  return (
    (
      <MUIChip
        {...props}
        data-testid={testId}
        ref={ref}
        className={cx(colorClassName, className, {
          [classes.labelBold]: labelWeight === 'bold',
          [classes.labelThin]: labelWeight === 'thin',
        })}
      />
    )
  );
}


export const LightChip = React.forwardRef<HTMLDivElement, LightChipProps>(LightChip_);
